import { z } from 'zod'

// ---- Live alerts ------------------------------------------------------------------------------

export const ALERT_TONES = ['info', 'warning', 'urgent'] as const
export type AlertTone = (typeof ALERT_TONES)[number]

/** Quick expiry picks on the phone form; `end_of_day` is 11:59 PM Central. */
export const ALERT_EXPIRIES = ['15m', '1h', 'end_of_day', 'none'] as const
export type AlertExpiry = (typeof ALERT_EXPIRIES)[number]

export const ALERT_MAX_LENGTH = 280

/** A full http(s) URL or a path on this site ("/map"). Protocol-relative "//x" is refused. */
const alertLinkSchema = z
  .string()
  .trim()
  .max(500)
  .refine(
    (v) => /^https?:\/\/[^\s]+$/i.test(v) || /^\/(?!\/)[^\s]*$/.test(v),
    'Use a full link (https://…) or a site path starting with /',
  )

export const alertInputSchema = z.object({
  message: z.string().trim().min(1, 'Write a message').max(ALERT_MAX_LENGTH),
  tone: z.enum(ALERT_TONES).default('info'),
  link: z.preprocess((v) => (v === '' ? null : v), alertLinkSchema.nullable()).optional(),
  /** Quick pick, counted from now. Omit on an edit to keep the current expiry. */
  expiry: z.enum(ALERT_EXPIRIES).optional(),
  eventId: z.preprocess((v) => (v === '' ? null : v), z.string().max(40).nullable()).optional(),
})
export type AlertInput = z.infer<typeof alertInputSchema>

// No defaults on an edit: a missing tone must keep the alert's tone, not reset it to info.
export const alertUpdateSchema = alertInputSchema.extend({ tone: z.enum(ALERT_TONES) }).partial()
export type AlertUpdate = z.infer<typeof alertUpdateSchema>

/** What the public sees: no author, no internal fields. */
export interface PublicLiveAlert {
  id: string
  message: string
  tone: AlertTone
  link: string | null
  startsAt: string
  expiresAt: string | null
}

export interface AdminLiveAlert extends PublicLiveAlert {
  active: boolean
  event: { id: string; name: string } | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

const TONE_RANK: Record<AlertTone, number> = { urgent: 0, warning: 1, info: 2 }

/** Most important first: urgent > warning > info, then newest. */
export function sortAlerts<T extends { tone: AlertTone; startsAt: string }>(alerts: T[]): T[] {
  return [...alerts].sort(
    (a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone] || b.startsAt.localeCompare(a.startsAt),
  )
}

/** Active at `at`: started, and either no expiry or not yet expired. */
export function alertIsActive(a: { startsAt: string; expiresAt: string | null }, at: string) {
  const t = Date.parse(at)
  return Date.parse(a.startsAt) <= t && (!a.expiresAt || Date.parse(a.expiresAt) > t)
}

// ---- Festival live page --------------------------------------------------------------------------

export interface LiveSession {
  id: string
  title: string
  kind: string
  startsAt: string
  endsAt: string | null
  location: string | null
}

export interface LiveStage {
  /** The stage name, or the location for sessions without a stage. */
  name: string
  now: LiveSession[]
  next: LiveSession | null
}

export interface LiveEvent {
  id: string
  slug: string
  name: string
  startsAt: string
  endsAt: string | null
  timezone: string
  venueName: string | null
  city: string | null
  /** upcoming (not today) | today (later today) | live (in progress) | ended */
  phase: 'upcoming' | 'today' | 'live' | 'ended'
  mapSlug: string | null
}

export interface PublicLiveView {
  at: string
  alerts: PublicLiveAlert[]
  event: LiveEvent | null
  stages: LiveStage[]
}

// ---- Vendor check-in -----------------------------------------------------------------------------

/** What anyone holding the code sees: enough to find the booth, nothing private. */
export interface CheckinVendorView {
  mode: 'vendor'
  displayName: string
  booth: { code: string | null; label: string | null }
  map: { name: string; slug: string | null }
  event: { name: string; startsAt: string; venueName: string | null } | null
  checkedIn: boolean
}

export interface CheckinStaffView {
  mode: 'staff'
  id: string
  displayName: string
  status: string
  booth: { code: string | null; label: string | null; boothType: string | null }
  map: { id: string; name: string; slug: string }
  event: { name: string; startsAt: string; venueName: string | null } | null
  application: { id: string; referenceCode: string; status: string; paid: boolean } | null
  organizationName: string | null
  notes: string | null
  checkedInAt: string | null
  checkedInBy: string | null
}

export type CheckinView = CheckinVendorView | CheckinStaffView

export interface CheckinBoardRow {
  id: string
  displayName: string
  boothCode: string | null
  boothLabel: string | null
  status: string
  applicationRef: string | null
  checkedInAt: string | null
  checkedInBy: string | null
  token: string
}

export interface CheckinBoard {
  maps: { id: string; name: string; slug: string; kind: string; eventName: string | null }[]
  map: { id: string; name: string; slug: string; status: string } | null
  event: { name: string; startsAt: string } | null
  counts: { total: number; checkedIn: number }
  items: CheckinBoardRow[]
}

export interface VendorCheckinCode {
  token: string
  displayName: string
  booth: { code: string | null; label: string | null }
  map: { name: string; slug: string; published: boolean }
  event: { name: string; startsAt: string; venueName: string | null } | null
  checkedInAt: string | null
}

/** The path a check-in QR code points at (the SPA route). */
export const checkinPath = (token: string) => `/checkin/v/${encodeURIComponent(token)}`
